import { useState } from "react";
import {
  useProgram, ProgramGate, ProgramLoading, NeedAuth, ProgramShell, useProgress, ProgressBar,
  CompleteButton, Block, Paragraphs, PromptList, AccordionItem,
} from "@/pages/programs/_gate";

function WeekBody({ w }) {
  return (
    <>
      <p className="font-serif italic text-base sm:text-lg text-muted-foreground leading-relaxed">{w.summary}</p>
      <Block label={`Modules · ${w.modules.length}`}>
        <div className="space-y-5 mt-2">
          {w.modules.map((m, i) => (
            <div key={i} className="flex items-start gap-4" data-testid={`week-${w.week}-module-${i + 1}`}>
              <span className="font-mono text-[10px] tracking-widest text-accent shrink-0 mt-1.5">{String(i + 1).padStart(2, "0")}</span>
              <div>
                <h4 className="font-serif text-lg sm:text-xl mb-1.5">{m.title}</h4>
                <Paragraphs text={m.detail} className="text-sm sm:text-base text-muted-foreground leading-relaxed" />
              </div>
            </div>
          ))}
        </div>
      </Block>
      <Block label="This week's practices" accent><PromptList items={w.practices} numbered /></Block>
      <Block label="End-of-week reflection"><p className="font-serif text-base sm:text-lg leading-relaxed">{w.reflection}</p></Block>
    </>
  );
}

export default function ReprogramReality() {
  const { loading, data, locked, needsAuth } = useProgram("/programs/reprogram-reality");
  const { done, toggle, isDone } = useProgress("reprogram_reality");
  const [open, setOpen] = useState(null);

  if (needsAuth) return <NeedAuth />;
  if (loading) return <ProgramLoading />;
  if (locked) return <ProgramGate productKey="reprogram_reality" priceLabel="Reprogram Your Reality · $199" purchaseCta="Unlock for $199" />;
  if (!data) return <ProgramLoading />;

  const current = open ?? (data.weeks.find((w) => !isDone(w.week))?.week ?? -1);

  return (
    <ProgramShell eyebrow={`Deep-dive · ${data.weeks.length} weeks`} title={data.title}>
      <p className="text-muted-foreground max-w-2xl mb-8 leading-relaxed">
        Eight weeks. Not to learn about reprogramming reality — to actually rewrite the operating system running yours. Work one week at a time: read each module, do the practices daily, and close the week with the reflection before moving on.
      </p>
      <ProgressBar done={done.length} total={data.weeks.length} label="weeks" />
      <div className="space-y-3">
        {data.weeks.map((w) => (
          <AccordionItem
            key={w.week}
            id={`week-${w.week}`}
            number={String(w.week).padStart(2, "0")}
            eyebrow={`Week ${w.week}`}
            title={w.title}
            open={current === w.week}
            done={isDone(w.week)}
            onToggle={() => setOpen(current === w.week ? -1 : w.week)}
          >
            <WeekBody w={w} />
            <CompleteButton done={isDone(w.week)} onClick={() => toggle(w.week)} testId={`week-${w.week}-complete`} />
          </AccordionItem>
        ))}
      </div>
    </ProgramShell>
  );
}
